import {
  Alert,
  Button,
  Donut,
  Funnel,
  Meter,
  Module,
  ModuleBody,
  ModuleHead,
  PageFrame,
  ProTable,
  Sparkbars,
  StatTile,
  StatusDot,
  TrendChart,
  useCollection,
  type CollectionColumnDef,
} from "@vjal/ui";
import type { DataTableColumn, DonutSlice, FunnelStage, TrendChartPoint } from "@vjal/ui";
import { Download } from "lucide-react";
import { useMemo, useState } from "react";
import { useWalkReadyMarker } from "../lib/walk-readiness";
import { HonestState } from "../components/honest-state";
import { getCoachingOrgId } from "../services/coaching-org-context";
import { CapabilityButton } from "../components/AccessState";
import { evaluateActionCapability } from "../domain/capabilities";
import type { CoachingRole } from "../domain/roles";
import { filterReadableReports } from "../domain/sensitive-surfaces";
import type {
  CoachingOrgAnalyticsSummary,
  CoachingOrgReportStatus,
  CoachingReportType
} from "../services/coaching-dtos";
import {
  useCoachingOrgAnalytics,
  useCoachingReportStatus,
  useCoachingReportTimeSeries,
  useReportExportCsv,
  useReportMarkReviewedMutation
} from "../hooks/useCoachingOrgAnalytics";
import { isLiveCoachingDataMode, useLiveCoachingList } from "../hooks/useLiveProductData";
import { liveReportTableRows } from "./product-live-rows";
import { runCsvExport } from "./exports/exports-helpers";
import type { ProductScreenContext, ProductScreenContract, TableRow } from "./product-route-contracts";

// ── Deterministic mock fixtures so the org surfaces render without a live API ──
// (the org surfaces are evidence-mode screens; mock mode supplies the rollups).
const mockSummary = {
  attendancePct: 82,
  submissionPct: 74,
  completionPct: 61,
  reportsReady: 4,
  reportsAwaitingReview: 2
};
const mockTrend: TrendChartPoint[] = [
  { label: "W1", value: 70 },
  { label: "W2", value: 78 },
  { label: "W3", value: 81 },
  { label: "W4", value: 85 }
];
const mockFunnel: FunnelStage[] = [
  { stage: "Enrolled", count: 26 },
  { stage: "Active", count: 22 },
  { stage: "Completed", count: 13 }
];
const mockDonut: DonutSlice[] = [
  { label: "Ready", value: 3, toneClass: "stroke-vjal-500" },
  { label: "Reviewed", value: 2, toneClass: "stroke-ok" },
  { label: "Generating", value: 1, toneClass: "stroke-mustard" }
];
const mockCohortRows: TableRow[] = [
  { id: "cohort-1", cohort: "Spring Leaders", participants: "18", attendance: "85%", submission: "78%", reports: "8 / 10" },
  { id: "cohort-2", cohort: "Summer Managers", participants: "8", attendance: "76%", submission: "66%", reports: "6 / 8" }
];

/** Ready team-admin rows for the follow-through queue (mock evidence mode). */
const followThroughFixtureRows: Array<TableRow & { reportType?: CoachingReportType; reviewedAt?: string | null; approvedAt?: string | null }> = [
  {
    id: "report-1",
    report: "Session 1 Team Report",
    session: "Session 1",
    cohort: "Spring Leaders",
    status: "ready",
    reportType: "team_admin",
    reviewedAt: null
  }
];

export function orgAdminHomeScreen(): ProductScreenContract {
  return {
    title: (role) => (role === "company_admin" ? "Org Overview" : "Org analytics overview"),
    primaryJob:
      "Read-only team-wide rollup: attendance, progress/completion, report status, and follow-through queue.",
    secondaryJob: "Charts are honest about ingestion: zero-filled rollups render explicit empty cards, never a 503.",
    workTitle: "Per-cohort outcomes",
    rows: mockCohortRows,
    columns: cohortColumns(),
    states: ["loading", "empty", "ingestion-pending", "read-only", "follow-through"],
    apiFamilies: ["analytics-service", "artifact-service"],
    roleDeltas:
      "Company admin sees their single company; super_admin/admin see org-wide; the follow-through queue and report status are D-3 scoped to team_admin/company_readable for company_admin.",
    extensionSlots: ["live ingestion charts", "cohort drill-down", "export bundle", "approve workflow (staff)"],
    mobilePosture: "KPI band reflows to a single column; charts are full-card width; the follow-through queue keeps Mark reviewed / Download visible.",
    evidenceRequirement: "Playwright must prove the 4 named infographics, the KPI band, the follow-through queue, and honest empty states for company_admin.",
    vjalComponents: ["Alert","Button","Donut","Funnel","Meter","Module","ModuleBody","ModuleHead","PageFrame","ProTable","Sparkbars","StatTile","StatusDot","TrendChart"],
    localCompositions: ["org-admin-home focused product surface", "CapabilityButton"],
    packageGapRefs: [],
    decisionHierarchy: {
      firstViewport: "KPI band of org outcomes (attendance, submission, completion, reports ready/awaiting).",
      primaryAction: "Work the follow-through queue: mark a ready team report reviewed or download it.",
      secondaryPanel: "Four infographics over the analytics rollup.",
      deferredSurface: "Live ingestion charts populate once Zoom/Jotform/Thinkific lanes are open; until then honest empty cards.",
      gatedControls: "Mark reviewed / Export CSV follow the capability layer; cohort_lead/participant never get them enabled."
    },
    requiredViewports: ["desktop", "mobile"],
    closureState: "focused",
    closureOwner: "VIL-20260616-0040",
    surfaceMode: "focused",
    customBody: OrgAdminHomeSurface
  };
}

function OrgAdminHomeSurface({ role }: ProductScreenContext) {
  const live = isLiveCoachingDataMode();
  // Keep the org selector explicit at this surface boundary. The shared live
  // transport also injects this value, but passing it into all three analytics
  // hooks makes the request contract visible and keeps query/cache scope stable.
  const orgId = getCoachingOrgId() ?? undefined;
  const analyticsQuery = { orgId };
  const summaryQuery = useCoachingOrgAnalytics(analyticsQuery);
  const trendQuery = useCoachingReportTimeSeries(analyticsQuery);
  const statusQuery = useCoachingReportStatus(analyticsQuery);
  const canViewFollowThrough = role === "super_admin" || role === "admin" || role === "company_admin";
  const liveReports = useLiveCoachingList("reports.list", { query: { orgId }, enabled: canViewFollowThrough });
  const markReviewed = useReportMarkReviewedMutation();
  const exportCsv = useReportExportCsv();
  const reviewDecision = evaluateActionCapability(role, "reports:mark-reviewed");
  const exportDecision = evaluateActionCapability(role, "exports:csv");
  const analyticsReads = [summaryQuery, trendQuery, statusQuery];
  const analyticsReady = analyticsReads.every((query) => !query.isLoading && (query.data !== undefined || query.isError));
  const followThroughReadReady = !canViewFollowThrough || (!liveReports.isLoading && (liveReports.isError || liveReports.denied || liveReports.isEmpty || liveReports.items.length > 0));
  const ready = !live || (analyticsReady && followThroughReadReady);
  useWalkReadyMarker(ready);
  const [downloadFailure, setDownloadFailure] = useState<{ reference?: string } | null>(null);
  const summary = summaryQuery.data;
  const analyticsError = [summaryQuery, trendQuery, statusQuery].find((query) => query.isError);
  const permissionDenied = analyticsError?.error && typeof analyticsError.error === "object" && "status" in analyticsError.error
    ? (analyticsError.error as { status?: unknown }).status === 403
    : false;
  const hasLiveFailure = live && Boolean(analyticsError);
  const trendPoints: TrendChartPoint[] = live
    ? (trendQuery.data?.points ?? []).map((point) => ({ label: `W${point.weekNumber}`, value: point.attendedRate }))
    : mockTrend;
  // D-3: a company_admin's report-status donut counts ONLY readable types.
  const statusSlices: DonutSlice[] = live ? reportStatusSlices(role, statusQuery.data) : mockDonut;
  const funnelStages: FunnelStage[] = live ? cohortFunnel(summary) : mockFunnel;
  const cohortRows: TableRow[] = live ? cohortTableRows(summary) : mockCohortRows;

  const attendancePct = live ? summary?.attendance.attendedRate ?? 0 : mockSummary.attendancePct;
  const submissionPct = live ? summary?.assignments.submissionRate ?? 0 : mockSummary.submissionPct;
  const completionPct = live ? summary?.courses.avgCompletionPercent ?? 0 : mockSummary.completionPct;
  const reportsReady = live ? readableReadyCount(role, statusQuery.data) : mockSummary.reportsReady;
  const reportsAwaiting = live ? readableAwaitingCount(role, statusQuery.data) : mockSummary.reportsAwaitingReview;
  const ingestionEmpty = live && (hasLiveFailure || (summary != null && summary.attendance.sessionsIngested === 0));

  // The fixture remains the deterministic mock/evidence queue. Live rows are
  // projected through the shared mapper before the queue's ready/unreviewed
  // predicate is applied; the backend remains authoritative for org scope.
  const followThrough = live
    ? liveReportTableRows(liveReports.items, []).filter(
      (row) => row.reportType === "team_admin" && row.status === "ready" && !row.reviewedAt && !row.approvedAt
    )
    : followThroughFixtureRows.filter(
      (row) => row.reportType === "team_admin" && row.status === "ready" && !row.reviewedAt && !row.approvedAt
    );

  const downloadReports = async () => {
    if (!exportDecision.allowed) return;
    setDownloadFailure(null);
    const result = await runCsvExport({ exportCsv: (type) => exportCsv.mutateAsync({ exportType: type }) }, role, "reports");
    if (result.failed) setDownloadFailure({ reference: result.reference });
  };
  const pageTitle = orgAdminHomeScreen().title(role);

  return (
    <PageFrame title={pageTitle} subtitle="Read-only rollup across your cohorts. Honest empty states until ingestion is live." className="col-span-12">
      {hasLiveFailure ? (
        <Alert variant={permissionDenied ? "warning" : "warning"}>
          {permissionDenied
            ? "You do not have permission to view organization analytics."
            : "Organization analytics are temporarily unavailable. No live rollup values are shown."}
        </Alert>
      ) : null}
      <Module span={12}>
        <ModuleHead title="Org analytics overview" subtitle="Read-only rollup across your cohorts. Honest empty states until ingestion is live." />
        <ModuleBody className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <StatTile label="Attendance" value={hasLiveFailure ? "—" : `${attendancePct}%`} meta="across cohorts" empty={ingestionEmpty} visual={<Meter value={attendancePct} label="Attendance percentage" />} />
          <StatTile label="Assignment submission" value={hasLiveFailure ? "—" : `${submissionPct}%`} meta="Jotform F10" empty={ingestionEmpty} visual={<Meter value={submissionPct} label="Assignment submission percentage" />} />
          <StatTile label="Course completion" value={hasLiveFailure ? "—" : `${completionPct}%`} meta="Thinkific D-5" empty={ingestionEmpty} visual={<Sparkbars data={trendPoints.map((point) => point.value)} />} />
          <StatTile label="Reports ready" value={hasLiveFailure ? "—" : String(reportsReady)} meta={`${reportsAwaiting} awaiting review`} empty={hasLiveFailure} />
        </ModuleBody>
      </Module>

      <Module span={6}>
        <ModuleHead title="Team attendance trend" subtitle="Live-session attendance % per week." />
        <ModuleBody><TrendChart data={trendPoints} unit="%" ariaLabel="Team attendance trend over weeks" emptyLabel="No attendance recorded yet — will populate once ingestion is live" /></ModuleBody>
      </Module>
      <Module span={6}>
        <ModuleHead title="Report status" subtitle="Team-admin report lifecycle (D-3 scoped)." />
        <ModuleBody><Donut data={statusSlices} ariaLabel="Report status breakdown" emptyLabel="No team-admin reports yet — will populate once ingestion is live" /></ModuleBody>
      </Module>
      <Module span={6}>
        <ModuleHead title="Cohort progress / completion funnel" subtitle="Enrolled to active to completed." />
        <ModuleBody><Funnel data={funnelStages} ariaLabel="Cohort progress and completion funnel" emptyLabel="No cohort progress yet — will populate once ingestion is live" /></ModuleBody>
      </Module>
      <Module span={6}>
        <ModuleHead title="Assignment submission rate" subtitle="Submitted % per the Jotform lane." />
        <ModuleBody className="space-y-2">
          {ingestionEmpty ? <Alert variant="info">No assignment submissions recorded yet — will populate once ingestion is live.</Alert> : <><Meter value={submissionPct} label="Assignment submission rate" /><p className="m-0 text-sm text-ink-muted">{submissionPct}% of expected assignments submitted across in-scope cohorts.</p></>}
        </ModuleBody>
      </Module>

      {canViewFollowThrough ? (
        <Module span={8}>
          <ModuleHead title="Follow-through queue" subtitle="Ready team-admin reports awaiting review." />
          <ModuleBody className="space-y-4">
            {downloadFailure ? (
              <div data-honest-posture="error">
                <Alert variant="error">
                  <span className="font-semibold">Export failed</span> — the CSV couldn't be generated; nothing was downloaded. Try again in a moment.
                  {downloadFailure.reference ? ` (reference: ${downloadFailure.reference})` : ""}
                </Alert>
              </div>
            ) : null}
            {live && liveReports.isLoading ? (
              <HonestState posture="loading" loadingVariant="rows" title="Loading follow-through reports" />
            ) : live && liveReports.denied ? (
              <HonestState
                posture="permission-denied"
                title="Follow-through reports are not available"
                message="Your role does not have permission to view reports awaiting review."
              />
            ) : live && liveReports.isError ? (
              <HonestState
                posture="error"
                title="Follow-through reports are unavailable"
                message="The report queue could not be loaded. Try again in a moment."
                reference={liveReports.reference}
              />
            ) : followThrough.length === 0 ? (
              <Alert variant="info">No team-admin reports are awaiting review.</Alert>
            ) : followThrough.map((row) => (
              <div key={row.id} className="grid gap-2 rounded-lg border border-line bg-white p-4 md:grid-cols-[1fr_auto] md:items-center">
                <div><p className="m-0 text-sm font-semibold text-strong">{String(row.report)}</p><p className="m-0 text-xs text-ink-muted">{`${String(row.cohort)} / ${String(row.session)}`}</p></div>
                <div className="flex flex-wrap items-center gap-2">
                  <CapabilityButton decision={reviewDecision} loading={markReviewed.isPending} onClick={() => markReviewed.mutate({ reportId: row.id })}>Mark reviewed</CapabilityButton>
                  <Button
                    type="button"
                    variant="secondary"
                    leftIcon={<Download size={16} />}
                    loading={exportCsv.isPending}
                    disabled={!exportDecision.allowed}
                    onClick={exportDecision.allowed ? () => void downloadReports() : undefined}
                  >Download</Button>
                </div>
              </div>
            ))}
            {markReviewed.isSuccess ? <StatusDot tone="ok" label="Report marked reviewed" /> : null}
          </ModuleBody>
        </Module>
      ) : null}
      <Module span={4}>
        <ModuleHead title="Cohorts" subtitle="Per-cohort outcomes deep-link into the cohort workspace." />
        <ModuleBody flush><OrgAdminCohortsTable rows={cohortRows} /></ModuleBody>
      </Module>
    </PageFrame>
  );
}


function OrgAdminCohortsTable({ rows }: { rows: TableRow[] }) {
  const columns: CollectionColumnDef<TableRow>[] = useMemo(
    () => [
      {
        id: "cohort",
        header: "Cohort",
        sortable: true,
        sortAccessor: (row) => String(row.cohort ?? ""),
        searchValue: (row) => String(row.cohort ?? ""),
        cell: (row) => String(row.cohort ?? ""),
      },
      {
        id: "participants",
        header: "Participants",
        cell: (row) => String(row.participants ?? ""),
      },
      {
        id: "attendance",
        header: "Attendance",
        cell: (row) => String(row.attendance ?? ""),
      },
      {
        id: "submission",
        header: "Submission",
        cell: (row) => String(row.submission ?? ""),
      },
      {
        id: "reports",
        header: "Reports",
        cell: (row) => String(row.reports ?? ""),
      },
    ],
    [],
  );
  const collection = useCollection({
    rows,
    columns,
    pageSize: 25,
    urlSync: false,
    getRowKey: (row) => String(row.id ?? row.cohort ?? Math.random()),
    views: "coaching-org-admin-cohorts",
  });

  return (
    <div className="rounded-xl border border-line bg-white">
      {/* F3: real @vjal/ui ProTable-1 recipe export for org-admin-home IR. */}
      <ProTable
        collection={collection}
        className="p-0"
        tableProps={{
          caption: "Per-cohort outcomes",
          emptyTitle: "No cohorts yet",
          emptyMessage: "Cohort outcomes appear here once analytics ingestion is live.",
        }}
        stableChipNames
      />
    </div>
  );
}

// ── helpers ──

function cohortColumns(): DataTableColumn<TableRow>[] {
  return [
    { key: "cohort", header: "Cohort" },
    { key: "participants", header: "Participants" },
    { key: "attendance", header: "Attendance" },
    { key: "submission", header: "Submission" },
    { key: "reports", header: "Reports" }
  ];
}

function reportStatusSlices(role: CoachingRole, status: CoachingOrgReportStatus | undefined): DonutSlice[] {
  if (!status) return [];
  // D-3: only count readable report types for this role.
  const readable = filterReadableReports(role, status.byType.map((entry) => ({ ...entry, reportType: entry.reportType, visibility: undefined, deliveryMode: undefined })));
  const ready = readable.reduce((sum, entry) => sum + entry.ready, 0);
  const reviewed = readable.reduce((sum, entry) => sum + entry.reviewed, 0);
  const generating = readable.reduce((sum, entry) => sum + entry.generating, 0);
  const failed = readable.reduce((sum, entry) => sum + entry.failed, 0);
  return [
    { label: "Ready", value: ready, toneClass: "stroke-vjal-500" },
    { label: "Reviewed", value: reviewed, toneClass: "stroke-ok" },
    { label: "Generating", value: generating, toneClass: "stroke-mustard" },
    { label: "Failed", value: failed, toneClass: "stroke-err" }
  ];
}

function readableReadyCount(role: CoachingRole, status: CoachingOrgReportStatus | undefined): number {
  if (!status) return 0;
  return filterReadableReports(role, status.byType.map((entry) => ({ reportType: entry.reportType, visibility: undefined, deliveryMode: undefined, ready: entry.ready })))
    .reduce((sum, entry) => sum + entry.ready, 0);
}

function readableAwaitingCount(role: CoachingRole, status: CoachingOrgReportStatus | undefined): number {
  if (!status) return 0;
  return status.byCohort.reduce((sum, entry) => sum + entry.awaitingReview, 0);
}

function cohortFunnel(summary: CoachingOrgAnalyticsSummary | undefined): FunnelStage[] {
  if (!summary) return [];
  const enrolled = summary.cohorts.reduce((sum, cohort) => sum + cohort.participantCount, 0);
  const completed = summary.progressDistribution.find((bucket) => bucket.bucket === "100")?.participantCount ?? 0;
  const active = Math.max(0, enrolled - (summary.progressDistribution.find((bucket) => bucket.bucket === "0-25")?.participantCount ?? 0));
  if (enrolled === 0) return [];
  return [
    { stage: "Enrolled", count: enrolled },
    { stage: "Active", count: active },
    { stage: "Completed", count: completed }
  ];
}

function cohortTableRows(summary: CoachingOrgAnalyticsSummary | undefined): TableRow[] {
  if (!summary) return [];
  return summary.cohorts.map((cohort) => ({
    id: cohort.cohortId,
    cohort: cohort.cohortName ?? `Cohort ${cohort.cohortId}`,
    participants: String(cohort.participantCount),
    attendance: `${cohort.attendedRate}%`,
    submission: `${cohort.submissionRate}%`,
    reports: `${cohort.reportsGenerated} / ${cohort.reportsTotal}`
  }));
}
